import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup } from '@angular/forms';

import { CurrentUserAndPermissions } from '®shared/current-user/current-user-and-permissions';
import { hasGrantRolePermissionForListId } from '®shared/permissions';
import { RoleId } from '®shared/permissions/roles';
import { UserJobRoleDescription, UserRecord } from '®shared/user';

import { ListOwnerAndIsPublisher } from '../metadata/owner-with-list-publisher-flag';

import {
  ListOwnersEditorHideModalAction,
  ListOwnersEditorSaveAction,
} from './list-owners-editor.actions';
import { ListOwnersEditorState } from './list-owners.state';

@Component({
  changeDetection: ChangeDetectionStrategy.Default,
  selector: 'rl-list-owners-editor',
  templateUrl: './list-owners-editor.component.html',
})
export class ListOwnersEditorComponent implements OnChanges {
  @Input() currentUser: CurrentUserAndPermissions;
  @Input() listOwnersEditorState: ListOwnersEditorState;
  @Input() listOwners: UserRecord[];

  @Output() save = new EventEmitter<ListOwnersEditorSaveAction>();
  @Output() close = new EventEmitter<ListOwnersEditorHideModalAction>();

  listOwnersForm: FormGroup;
  saveWithPublisherRoleClicked = false;
  UserJobRoleDescription = UserJobRoleDescription;

  constructor(private formBuilder: FormBuilder) {
    this.listOwnersForm = this.formBuilder.group({
      owners: this.formBuilder.array([]),
    });
  }

  populateForm() {
    (this.listOwners || []).forEach((listOwner, index) => {
      this.listOwnersFormArray.setControl(index, new FormControl(listOwner));
    });
  }

  ngOnChanges(changes?: SimpleChanges) {
    if (changes && changes.listOwners) {
      this.populateForm();
    }
  }

  get listOwnersFormArray(): FormArray {
    return this.listOwnersForm.get('owners') as FormArray;
  }

  get listOwnersFormArrayEmpty(): boolean {
    return this.listOwnersFormArray.value.length === 0;
  }

  get hasInviteListPublishersPermission(): boolean {
    return hasGrantRolePermissionForListId(
      RoleId.LIST_PUBLISHER,
      this.listOwnersEditorState.listId,
      this.currentUser.permissions,
    );
  }

  onCancel(): void {
    if (this.listOwnersEditorState.isSaving) {
      return;
    }

    this.close.emit(new ListOwnersEditorHideModalAction());
  }

  onSubmit(withPublisherRole: boolean): void {
    if (this.listOwnersEditorState.isSaving) {
      return;
    }

    this.saveWithPublisherRoleClicked = withPublisherRole;
    const ownersWithPublisherFlags: ListOwnerAndIsPublisher[] =
      this.listOwnersFormArray.value.map((owner: UserRecord) => {
        return { owner, isPublisher: withPublisherRole };
      });

    this.save.emit(
      new ListOwnersEditorSaveAction({
        listId: this.listOwnersEditorState.listId,
        ownersWithPublisherFlags,
      }),
    );
  }

  addListOwner(listOwner: UserRecord) {
    if (this.listOwnersEditorState.isSaving) {
      return;
    }

    // Currently, we only allow one list owner to be assigned
    this.listOwnersFormArray.setControl(0, new FormControl(listOwner));
  }

  removeListOwner(index: number) {
    if (this.listOwnersEditorState.isSaving) {
      return;
    }

    this.listOwnersFormArray.removeAt(index);
  }
}
